import { isEqualColors } from '../color';
import { isPhrasingContent, isPresentationalPhrasingContent } from './content';
import { getComputedStyle, simplifyElement } from './element';
import { findAncestor, isElement, isHTMLOrSVGElement } from './node';
import { paintNodesInRange } from './paint';

export interface StyleSpec {
  stylePropertyName?: string;
  initialStylePropertyValue?: string;
  mapStylePropertyValue?: StylePropertyValueProjection;
  fontElementAttributeName?: string;
  mapFontElementAttributeValue?: FontElementAttributeValueProjection;
  mapValue?: ValueProjection;
  isValueEqual?: (a: string, b: string) => boolean;
  shouldPaintElementIfValueDiffers?: (node: Node) => boolean;
}

export type StylePropertyValueProjection = (
  stylePropertyValue: string,
  stylePropertyName: string,
  element: HTMLElement | SVGElement,
  container: Node,
) => string;

export type FontElementAttributeValueProjection = (
  fontElementAttributeValue: string,
  fontElementAttributeName: string,
  element: HTMLFontElement,
  container: Node,
) => string;

export type ValueProjection = (value: string) => string;

export function getCSSPropertyValue(element: Element, property: string) {
  const computedStyle = getComputedStyle(element);
  if (computedStyle === null) {
    return null;
  }
  return computedStyle.getPropertyValue(property);
}

export function createMapToComputedStyle(
  computedStylePropertyName: string,
): StylePropertyValueProjection & FontElementAttributeValueProjection {
  return (_value: any, _name: any, element: HTMLElement | SVGElement) => {
    const window = element.ownerDocument && element.ownerDocument.defaultView;
    return window ? window.getComputedStyle(element).getPropertyValue(computedStylePropertyName) : '';
  };
}

const CURRENT_COLOR_SPEC = {
  stylePropertyName: 'color',
  fontElementAttributeName: 'color',
  isValueEqual: isEqualColors,
};

function maybeMapValue(spec: StyleSpec, newStylePropertyValue: string): string {
  return spec.mapValue ? spec.mapValue(newStylePropertyValue) : newStylePropertyValue;
}

function isValueStrictEqual(a: string, b: string): boolean {
  return a === b;
}

export function isFontElement(node: Node | null): node is HTMLFontElement {
  return isElement(node) && node.tagName === 'FONT';
}

export function hasInheritedStyleForNode(container: Node, node: Node, spec: StyleSpec): boolean {
  return !!getInheritedStyleForNode(container, node, spec);
}

export function getInheritedStyleForNode(container: Node, node: Node, spec: StyleSpec): string {
  const element = findAncestor(
    node,
    (ancestor =>
      isHTMLOrSVGElement(ancestor) &&
      hasStyleForElement(container, ancestor, spec)) as typeof isHTMLOrSVGElement,
    container,
  );
  return element ? getStyleForNode(container, element, spec) : '';
}

export function hasStyleForElement(
  container: Node,
  element: HTMLElement | SVGElement,
  spec: StyleSpec,
): boolean {
  return !!getStyleForNode(container, element, spec);
}

export function getStyleForNode(container: Node, element: HTMLElement | SVGElement, spec: StyleSpec): string {
  const {
    stylePropertyName,
    initialStylePropertyValue,
    mapStylePropertyValue,
    fontElementAttributeName,
    mapFontElementAttributeValue,
    mapValue,
  } = spec;

  if (stylePropertyName) {
    let value = element.style.getPropertyValue(stylePropertyName);

    if (value.toLowerCase() === 'currentcolor') {
      if (stylePropertyName.toLowerCase() === 'color') {
        // If currentColor is used as the value of the color property, use the inherited value.
        value = 'inherit';
      } else {
        // Use the color property.
        value = getInheritedStyleForNode(container, element, CURRENT_COLOR_SPEC);
      }
    }

    if (value.toLowerCase() === 'inherit') {
      value = element.parentNode ? getInheritedStyleForNode(container, element.parentNode, spec) : '';
    }

    if (value.toLowerCase() === 'initial') {
      value = initialStylePropertyValue || '';
    }

    if (value.toLowerCase() === 'unset') {
      value = '';
    }

    if (value && mapStylePropertyValue) {
      value = mapStylePropertyValue(value, stylePropertyName, element, container);
    }

    if (mapValue) {
      value = mapValue(value);
    }

    if (value) {
      return value;
    }
  }

  if (fontElementAttributeName && isFontElement(element) && element.hasAttribute(fontElementAttributeName)) {
    let value = element.getAttribute(fontElementAttributeName) || '';

    if (mapFontElementAttributeValue) {
      value = mapFontElementAttributeValue(value, fontElementAttributeName, element, container);
    }

    if (mapValue) {
      value = mapValue(value);
    }

    if (value) {
      return value;
    }
  }

  return '';
}

export function removeStyleFromElement(element: HTMLElement | SVGElement, spec: StyleSpec): Node | null {
  const { stylePropertyName, fontElementAttributeName } = spec;
  let shouldSimplify = false;

  if (stylePropertyName && element.style.getPropertyValue(stylePropertyName)) {
    element.style.removeProperty(stylePropertyName);
    shouldSimplify = element.tagName === 'SPAN';
  }

  if (fontElementAttributeName && isFontElement(element) && element.hasAttribute(fontElementAttributeName)) {
    element.removeAttribute(fontElementAttributeName);
    shouldSimplify = element.attributes.length === 0;
  }

  return shouldSimplify ? simplifyElement(element) : element;
}

export function removeStyleFromRange(container: Node, range: Range, spec: StyleSpec): Range {
  return paintNodesInRange(container, range, {
    shouldPaintNode: node => hasInheritedStyleForNode(container, node, spec),
    shouldSplitElement: element => isPresentationalPhrasingContent(element),
    paintElement: element => (isHTMLOrSVGElement(element) ? removeStyleFromElement(element, spec) : element),
  });
}

export function applyStyleToElement(
  element: HTMLElement | SVGElement,
  spec: StyleSpec,
  newStylePropertyValue: string,
): void {
  const { stylePropertyName } = spec;
  if (!stylePropertyName) {
    return;
  }

  const newValue = maybeMapValue(spec, newStylePropertyValue);
  element.style.setProperty(stylePropertyName, newValue);
}

export function applyStyleToRange(
  container: Node,
  range: Range,
  spec: StyleSpec,
  newStylePropertyValue: string,
): Range {
  const { stylePropertyName } = spec;
  if (!stylePropertyName) {
    return range;
  }

  const newValue = maybeMapValue(spec, newStylePropertyValue);
  const isValueEqual = spec.isValueEqual || isValueStrictEqual;
  let newRange = range;

  // Remove existing styles having a different value.
  newRange = paintNodesInRange(container, newRange, {
    shouldPaintNode: node => {
      const value = getInheritedStyleForNode(container, node, spec);
      return !!value && !isValueEqual(newValue, value);
    },
    shouldSplitElement: element => isPresentationalPhrasingContent(element),
    paintElement: element => (isHTMLOrSVGElement(element) ? removeStyleFromElement(element, spec) : element),
  });

  const shouldPaintElementIfValueDiffers = spec.shouldPaintElementIfValueDiffers
    ? spec.shouldPaintElementIfValueDiffers
    : () => false;

  // Apply the new styles.
  newRange = paintNodesInRange(container, newRange, {
    shouldPaintNode: node =>
      (isPhrasingContent(node) || shouldPaintElementIfValueDiffers(node)) &&
      !isValueEqual(newValue, getInheritedStyleForNode(container, node, spec)),
    createPaintedElement: document => {
      const span = document.createElement('span');
      span.style.setProperty(stylePropertyName, newValue);
      return span;
    },
    paintElement: element => {
      if (isHTMLOrSVGElement(element)) {
        element.style.setProperty(stylePropertyName, newValue);
      }

      return element;
    },
  });

  return newRange;
}
